/**
 * Support-desk client for the ops dashboard.
 *
 * Mirrors `api.ts` deliberately — the generated `SupportAdminApi` imported directly, an
 * axios instance carrying the operator bearer token, and a 401 interceptor that clears the
 * session. It is a separate file rather than more functions in `api.ts` only because that
 * file is already the queue console's own client and mixing two domains in one 700-line
 * module makes both harder to change.
 *
 * The merchant-facing `SupportApi` is NOT used here: it answers to a Shopify session, not
 * an operator token, and returns only public messages.
 */

import axios, {
  type AxiosError,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import {
  ListSupportAdminTicketsPriorityEnum,
  ListSupportAdminTicketsStatusEnum,
  ListSupportAdminTicketsTierEnum,
  SupportAdminApi,
  type ListSupportAdminTicketsCategoryEnum,
  type ListSupportAdminTicketsScopeEnum,
} from '../../../../libs/api-client/src/generated/api/support-admin-api';
import {
  CreateSupportAdminMessageDtoStatusEnum,
  UpdateSupportTicketCategoryDtoCategoryEnum,
  UpdateSupportTicketPriorityDtoPriorityEnum,
  UpdateSupportTicketStatusDtoStatusEnum,
} from '../../../../libs/api-client/src/generated/models';
import { Configuration } from '../../../../libs/api-client/src/generated/configuration';
import { clearToken, getToken } from './auth';

// ── Types ───────────────────────────────────────────────────────────────────

// Re-exported from the generated client rather than restated here. Hand-copied shapes are
// how a dashboard quietly starts rendering a field the API stopped sending.
export type {
  SupportAdminDocumentDto as SupportDocument,
  SupportAdminShopStateDto as SupportShopState,
  SupportAdminMessageDto as SupportMessage,
  SupportAdminTicketDetailDto as SupportTicketDetail,
  SupportAdminTicketListResponseDto as SupportTicketList,
  SupportAdminTicketSummaryDto as SupportTicketRow,
  SupportAgentDto as SupportAgent,
} from '../../../../libs/api-client/src/generated/models';

/** Query for the agent queue. Mirrors `ListSupportAdminTicketsQueryDto`. */
export interface SupportQueueFilters {
  page?: number;
  limit?: number;
  status?: ListSupportAdminTicketsStatusEnum;
  category?: ListSupportAdminTicketsCategoryEnum;
  priority?: ListSupportAdminTicketsPriorityEnum;
  assignee?: string;
  tier?: ListSupportAdminTicketsTierEnum;
  overdue?: boolean;
  search?: string;
  scope?: ListSupportAdminTicketsScopeEnum;
}

// ── Client ──────────────────────────────────────────────────────────────────

/**
 * The API root this dashboard talks to.
 *
 * Exported because the document lookup renders links straight AT the API — the per-document
 * PDF routes are public and secured by the unguessable id, so an anchor is the whole of what
 * that download needs, and routing those bytes through an authenticated fetch would buy
 * nothing but a blob dance.
 */
export function getApiBasePath(): string {
  const base = import.meta.env['VITE_API_BASE_URL'] ?? '';
  return base ? `${base.replace(/\/$/, '')}/api` : '/api';
}

function createAuthedAxios() {
  const instance = axios.create();
  instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const token = getToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  });
  instance.interceptors.response.use(
    (r: AxiosResponse) => r,
    (err: AxiosError) => {
      if (err.response?.status === 401) {
        clearToken();
        window.location.href = '/login';
      }
      throw err;
    },
  );
  return instance;
}

function client(): SupportAdminApi {
  const basePath = getApiBasePath();
  return new SupportAdminApi(
    new Configuration({ basePath }),
    basePath,
    createAuthedAxios(),
  );
}

/**
 * Whether a failed send was refused because the ticket moved, rather than because it broke.
 *
 * The API answers 409 to a reply carrying an `expectedStateToken` that no longer matches. The
 * two failures need opposite reactions from the agent — take the update and re-read, versus
 * retry — so the composer has to tell them apart, and status is the only thing that does.
 */
export function isTicketConflict(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 409;
}

// ── Endpoints ───────────────────────────────────────────────────────────────

export async function fetchSupportTickets(filters: SupportQueueFilters) {
  const { data } = await client().listSupportAdminTickets(filters);
  return data;
}

export async function fetchSupportTicket(id: string) {
  const { data } = await client().getSupportAdminTicket({ id });
  return data;
}

export async function fetchSupportAgents() {
  const { data } = await client().listSupportAgents();
  return data;
}

/**
 * @param expectedStateToken - The `stateToken` off the ticket this reply was written against.
 *   The API answers 409 rather than sending, if anyone changed the ticket in between.
 */
export async function replyToTicket(
  id: string,
  body: string,
  status?: CreateSupportAdminMessageDtoStatusEnum,
  expectedStateToken?: string,
) {
  const { data } = await client().replyToSupportTicket({
    id,
    createSupportAdminMessageDto: { body, status, expectedStateToken },
  });
  return data;
}

/** @param expectedStateToken - As on {@link replyToTicket}. */
export async function addTicketNote(
  id: string,
  body: string,
  expectedStateToken?: string,
) {
  const { data } = await client().addSupportTicketNote({
    id,
    createSupportAdminMessageDto: { body, expectedStateToken },
  });
  return data;
}

/**
 * @param requestReviewAsk - Read only when `status` is RESOLVED: send the App Store review
 *   invitation three days from now. Opt-in, so the default sends nothing.
 */
export async function setTicketStatus(
  id: string,
  status: UpdateSupportTicketStatusDtoStatusEnum,
  requestReviewAsk = false,
) {
  const { data } = await client().updateSupportTicketStatus({
    id,
    updateSupportTicketStatusDto: { status, requestReviewAsk },
  });
  return data;
}

export async function setTicketPriority(
  id: string,
  priority: UpdateSupportTicketPriorityDtoPriorityEnum,
) {
  const { data } = await client().updateSupportTicketPriority({
    id,
    updateSupportTicketPriorityDto: { priority },
  });
  return data;
}

export async function setTicketCategory(
  id: string,
  category: UpdateSupportTicketCategoryDtoCategoryEnum,
) {
  const { data } = await client().updateSupportTicketCategory({
    id,
    updateSupportTicketCategoryDto: { category },
  });
  return data;
}

/** Narrows a string to a ticket category, or `undefined`. */
export function toTicketCategory(
  value: string,
): UpdateSupportTicketCategoryDtoCategoryEnum | undefined {
  return Object.values(UpdateSupportTicketCategoryDtoCategoryEnum).find(
    (member) => member === value,
  );
}

export async function setTicketAssignee(id: string, assignee: string | null) {
  const { data } = await client().updateSupportTicketAssignee({
    id,
    updateSupportTicketAssigneeDto: { assignee },
  });
  return data;
}

// ── Narrowing ───────────────────────────────────────────────────────────────
//
// A `<select>` hands back a plain string. These turn one into the generated enum member,
// or `undefined` when it is not one — so a stale option in a cached page is dropped here
// rather than reaching the API as a value it will reject with a 400 the operator cannot
// interpret. Guards, not assertions: the whole point is that the value might not be valid.

/** Narrows a string to a status a reply may set, or `undefined`. */
export function toReplyStatus(
  value: string,
): CreateSupportAdminMessageDtoStatusEnum | undefined {
  return Object.values(CreateSupportAdminMessageDtoStatusEnum).find(
    (member) => member === value,
  );
}

/** Narrows a string to a settable ticket status, or `undefined`. */
export function toTicketStatus(
  value: string,
): UpdateSupportTicketStatusDtoStatusEnum | undefined {
  return Object.values(UpdateSupportTicketStatusDtoStatusEnum).find(
    (member) => member === value,
  );
}

/** Narrows a string to a ticket priority, or `undefined`. */
export function toTicketPriority(
  value: string,
): UpdateSupportTicketPriorityDtoPriorityEnum | undefined {
  return Object.values(UpdateSupportTicketPriorityDtoPriorityEnum).find(
    (member) => member === value,
  );
}

/** Narrows a filter string to a queue status filter, or `undefined` for "any". */
export function toFilterStatus(
  value: string,
): ListSupportAdminTicketsStatusEnum | undefined {
  return Object.values(ListSupportAdminTicketsStatusEnum).find(
    (member) => member === value,
  );
}

/** Narrows a filter string to a plan tier, or `undefined` for "any". */
export function toFilterTier(
  value: string,
): ListSupportAdminTicketsTierEnum | undefined {
  return Object.values(ListSupportAdminTicketsTierEnum).find(
    (member) => member === value,
  );
}

/** Narrows a filter string to a priority, or `undefined` for "any". */
export function toFilterPriority(
  value: string,
): ListSupportAdminTicketsPriorityEnum | undefined {
  return Object.values(ListSupportAdminTicketsPriorityEnum).find(
    (member) => member === value,
  );
}

/**
 * The shop as it stands right now, for the ticket's live panel.
 *
 * @param id - The ticket to read the shop of.
 */
export async function fetchTicketShopState(id: string) {
  const { data } = await client().getSupportTicketShopState({ id });
  return data;
}

/**
 * Finds the documents a merchant could be quoting.
 *
 * @param q - The document number, order name or order id the agent typed.
 * @param shopId - Scope to one shop. Always pass it when the lookup started from a ticket:
 *   it is what lets the API read the number through that shop's own prefix and year digits.
 */
export async function lookupDocuments(q: string, shopId?: string) {
  const { data } = await client().lookupSupportDocuments({ q, shopId });
  return data;
}

/**
 * Fetches a support attachment as a blob.
 *
 * Not the generated client: it leaves `responseType` alone, so axios parses the body as text
 * and hands back a corrupted string for anything that is not UTF-8 — which is every PNG and
 * PDF a merchant attaches. Not a plain anchor either, because this route is behind the
 * operator token and an `<a href>` carries no Authorization header.
 *
 * @param id - The attachment to fetch.
 */
export async function downloadAttachment(id: string): Promise<Blob> {
  const { data } = await createAuthedAxios().get<Blob>(
    `${getApiBasePath()}/v1/support-admin/attachments/${id}`,
    { responseType: 'blob' },
  );
  return data;
}
